import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { NativeChessBoard } from './NativeChessBoard';
import { useTrainingSession } from '@/hooks/useTrainingSession';
import type { TrainingPosition } from '@/hooks/useTrainingPositions';
import { ArrowLeft, ArrowRight, SkipForward, CheckCircle, XCircle, Target } from 'lucide-react';
import { GameState, Position } from '@/lib/chess-engine';
import { fenToGameState } from '@/lib/fen-parser';
import { toast } from '@/hooks/use-toast';

interface MoveTrainerProps {
  positions: TrainingPosition[];
  onComplete: () => void;
}

export function MoveTrainer({ positions, onComplete }: MoveTrainerProps) {
  const {
    currentPosition,
    currentIndex,
    totalPositions,
    attempts,
    isComplete,
    recordAttempt,
    nextPosition,
    previousPosition,
    skipPosition,
    getSessionStats
  } = useTrainingSession(positions);

  const [gameState, setGameState] = useState<GameState | null>(null);
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [showSolution, setShowSolution] = useState(false);
  const [attemptMade, setAttemptMade] = useState(false);

  useEffect(() => {
    if (currentPosition) {
      const state = fenToGameState(currentPosition.position_fen);
      setGameState(state);
      setSelectedSquare(null);
      setShowSolution(false);
      setAttemptMade(false);
    }
  }, [currentPosition]);

  useEffect(() => {
    if (isComplete) {
      const stats = getSessionStats();
      toast({
        title: 'Training Complete! 🎉',
        description: `You got ${stats.correct} out of ${stats.total} correct (${stats.accuracy}%)`,
      });
      onComplete();
    }
  }, [isComplete, getSessionStats, onComplete]);

  const handleMove = async (from: string, to: string) => {
    if (!gameState || !currentPosition || attemptMade) return;
    const moveUci = `${from}${to}`;
    const isCorrect = moveUci === currentPosition.correct_move_uci;
    await recordAttempt(moveUci, moveUci, isCorrect);
    setAttemptMade(true);
    setShowSolution(true);
    toast({
      title: isCorrect ? 'Correct! ✓' : 'Incorrect',
      description: isCorrect ? 'Well done!' : `Best move: ${currentPosition.correct_move_san}`,
      variant: isCorrect ? 'default' : 'destructive',
    });
  };

  const handleSquareClick = (position: Position) => {
    if (!gameState || attemptMade) return;
    if (!selectedSquare) {
      setSelectedSquare(position);
    } else {
      const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
      // Position is [row, col] tuple
      const from = `${files[selectedSquare[1]]}${8 - selectedSquare[0]}`;
      const to = `${files[position[1]]}${8 - position[0]}`;
      handleMove(from, to);
      setSelectedSquare(null);
    }
  };

  const handleNext = () => {
    nextPosition();
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty < 1200) return 'bg-green-500';
    if (difficulty < 1600) return 'bg-yellow-500';
    if (difficulty < 2000) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getMasteryColor = (level: number) => {
    if (level === 0) return 'bg-gray-400';
    if (level <= 2) return 'bg-yellow-500';
    if (level <= 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  if (!currentPosition || !gameState) {
    return <div>Loading...</div>;
  }

  const stats = getSessionStats();
  const progress = ((currentIndex + 1) / totalPositions) * 100;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Position {currentIndex + 1} of {totalPositions}</span>
          <span className="text-sm text-muted-foreground">{stats.accuracy}% accuracy</span>
        </div>
        <Progress value={progress} className="h-2" />
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">{currentPosition.mistake_type.replace('_', ' ')}</Badge>
            <Badge variant="outline" className="capitalize">{currentPosition.position_category}</Badge>
            <Badge className={getDifficultyColor(currentPosition.difficulty_rating)}>{currentPosition.difficulty_rating}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-sm">
              <Target className="w-4 h-4" />
              <span>Mastery: {currentPosition.mastery_level}/5</span>
              <div className={`w-2 h-2 rounded-full ${getMasteryColor(currentPosition.mastery_level)}`} />
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2">Find the best move for {currentPosition.player_color}</p>
      </Card>

      <Card className="p-4">
        <NativeChessBoard
          gameState={gameState}
          onSquareClick={handleSquareClick}
          selectedSquare={selectedSquare}
          flipped={currentPosition.player_color === 'black'}
        />
      </Card>

      {showSolution && (
        <Card className="p-4 bg-muted">
          <div className="flex items-start gap-3">
            {attempts[attempts.length - 1]?.isCorrect ? (
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 text-destructive mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-medium">{attempts[attempts.length - 1]?.isCorrect ? 'Correct!' : 'Incorrect'}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Best move: <span className="font-mono">{currentPosition.correct_move_san}</span>
                {' '}({currentPosition.evaluation_loss} centipawn loss in the original game)
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" onClick={previousPosition} disabled={currentIndex === 0}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        <div className="flex gap-2">
          {!attemptMade && (
            <Button variant="outline" onClick={skipPosition}>
              <SkipForward className="w-4 h-4 mr-2" />
              Skip
            </Button>
          )}
          {attemptMade && (
            <Button onClick={handleNext}>
              {currentIndex < totalPositions - 1 ? (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                'Finish'
              )}
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-green-500">{stats.correct}</p>
            <p className="text-sm text-muted-foreground">Correct</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.accuracy}%</p>
            <p className="text-sm text-muted-foreground">Accuracy</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.avgTime}s</p>
            <p className="text-sm text-muted-foreground">Avg Time</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
